// Audio cues for the workout timers.
//
// iOS is the whole reason this file exists. Safari creates every AudioContext
// in the `suspended` state and will only resume it from inside a user-gesture
// call stack — a resume() attempted from a setInterval/setTimeout callback is
// silently ignored. A countdown that builds its context when it wants to beep
// therefore never makes a sound on an iPhone, which is exactly what the timers
// used to do.
//
// So: one shared context, unlocked on the tap that STARTS a timer, reused by
// the callback that finishes it. unlockAudio() is idempotent and cheap once
// the context is running, so start handlers can call it unconditionally.

type WebkitWindow = Window & { webkitAudioContext?: typeof AudioContext };

// A phase that ended while the screen was off still gets its cue the moment you
// look at the phone — that's the point of the whole exercise. But not if it
// ended ages ago: chirping when you reopen the app an hour later is just noise.
// Callers compare (now − endedAt) against this before cueing.
export const MISSED_CUE_GRACE_MS = 60_000;

let ctx: AudioContext | null = null;

function audioCtx(): AudioContext | null {
  if (typeof window === 'undefined') return null;
  if (ctx && ctx.state !== 'closed') return ctx;
  const Ctor = window.AudioContext ?? (window as WebkitWindow).webkitAudioContext;
  if (!Ctor) return null;
  try {
    ctx = new Ctor();
  } catch {
    ctx = null;
  }
  return ctx;
}

// unlockAudio must be called synchronously from a user gesture — a click/tap
// handler, before any await. Resuming is not enough on iOS; the audio session
// only truly opens once a buffer has been pushed through the context, so we
// play one silent sample.
export function unlockAudio(): void {
  const c = audioCtx();
  if (!c) return;
  try {
    void c.resume();
    const src = c.createBufferSource();
    src.buffer = c.createBuffer(1, 1, 22050);
    src.connect(c.destination);
    src.start(0);
  } catch {
    // No audio available — timers still run, just silently.
  }
}

// tone schedules one sine blip. The gain ramps exist because starting or
// stopping a sine at full amplitude produces an audible click.
function tone(c: AudioContext, freq: number, offsetSec: number, durSec: number, peak: number): void {
  const osc = c.createOscillator();
  const gain = c.createGain();
  const at = c.currentTime + offsetSec;
  osc.type = 'sine';
  osc.frequency.setValueAtTime(freq, at);
  gain.gain.setValueAtTime(0, at);
  gain.gain.linearRampToValueAtTime(peak, at + 0.015);
  gain.gain.linearRampToValueAtTime(0, at + durSec);
  osc.connect(gain).connect(c.destination);
  osc.start(at);
  osc.stop(at + durSec + 0.02);
}

// play resumes a context that the OS suspended while we were backgrounded,
// then hands it to the caller. Returns null when there's no audio at all.
function play(): AudioContext | null {
  const c = audioCtx();
  if (!c) return null;
  if (c.state === 'suspended') void c.resume();
  return c;
}

// tick — the per-second countdown blip for the last few seconds of a phase.
export function tick(): void {
  const c = play();
  if (!c) return;
  try {
    tone(c, 660, 0, 0.09, 0.18);
  } catch {
    // ignore
  }
}

// cueDone — "this is over": rest is up, hold is finished, workout complete.
// Two rising tones, loud enough to hear across a gym with music on.
export function cueDone(): void {
  const c = play();
  if (c) {
    try {
      tone(c, 880, 0, 0.16, 0.3);
      tone(c, 1320, 0.18, 0.3, 0.3);
    } catch {
      // ignore
    }
  }
  vibrate([180, 80, 180]);
}

// cuePhase — a transition *inside* a running interval (work → rest, next
// round). Deliberately softer than cueDone so the two aren't confused.
export function cuePhase(): void {
  const c = play();
  if (c) {
    try {
      tone(c, 880, 0, 0.18, 0.26);
    } catch {
      // ignore
    }
  }
  vibrate(120);
}

// Vibration is a no-op on iOS (Safari doesn't implement it) but costs nothing
// and is a real win on Android.
function vibrate(pattern: number | number[]): void {
  try {
    navigator.vibrate?.(pattern);
  } catch {
    // ignore
  }
}

// Test seam — drops the shared context so each test starts clean.
export function __resetAudioForTests(): void {
  ctx = null;
}
